import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { backendApiServiceUrl, createApiDefaults } from './base';
import * as yup from 'yup';
import { axiosBaseQueryWithAuth } from './axios';
import { AbortController } from 'node-abort-controller';

interface MarketShareSegment {
  desktop_searches: number;
  mobile_searches: number;
  tablet_searches: number;
  segment_id: string;
  segment_name: string;
  total_searches: number;
}

interface Domain {
  top_data: string;
  totalclicks: string;
}

interface MasterDataResponse {
  market_share_segment: MarketShareSegment[];
  domain: Domain[];
}

interface savedSegment {
  text: string;
  value: string;
  definition: string;
  additional_info: string;
  id: string | number; // Use 'number' if 'id' is a number, 'string' if it's a string
}

export const commonApi = createApi({
  ...createApiDefaults,
  refetchOnFocus: false,
  baseQuery: axiosBaseQueryWithAuth({ baseUrl: backendApiServiceUrl }),
  reducerPath: 'common', // this is a legacy name. if this is changed, everyone will be logged out
  tagTypes: ['common', 'trends', 'CR_LIST', 'CR_PAGE_CONTEXT'],

  endpoints: (builder) => ({
    getMasterData: builder.query<MasterDataResponse, string[]>({
      query: (params) => {
        return {
          url: `master-data`,
          method: 'POST',
          data: {
            params,
          },
        };
      },
      providesTags: ['common'],
    }),
    getTrendsData: builder.query({
      query: (params) => {
        return {
          url: `explore/trends`,
          method: 'POST',
          data: {
            params,
          },
        };
      },
      providesTags: ['trends'],
    }),
    getCRList: builder.query({
      query: (params) => {
        return {
          url: `recommendations/content-recommendations`,
          method: 'POST',
          data: {
            ...params,
          },
        };
      },
      providesTags: ['CR_LIST'],
    }),
    generatePageContext: builder.query({
      query: (params) => {
        return {
          url: `recommendations/content-recommendations/page-context`,
          method: 'POST',
          data: {
            ...params,
          },
        };
      },
      providesTags: ['CR_PAGE_CONTEXT'],
    }),
    getCustomerInfo: builder.query({
      query: () => {
        return {
          url: `customer-info`,
          method: 'GET',
        };
      },
      providesTags: ['common'],
    }),
    getSegmentDataWithType: builder.query<savedSegment[], string>({
      query: (type) => {
        return {
          url: `segment?type=${type}`,
          method: 'GET',
        };
      },
      transformResponse: (baseResponse: any) => {
        const data = baseResponse?.data?.map((obj) => {
          return {
            text: obj.name,
            value: obj.name,
            definition: obj.definition,
            additional_info: obj.additional_info,
            id: obj.id,
          };
        });
        return data;
      },
      providesTags: ['common'],
    }),
  }),
});

export async function dispatchMasterDataQuery(dispatch, params) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getMasterData.initiate(params));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching master-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export function dispatchTrendsDataQuery(dispatch, params) {
  const abortController = new AbortController();
  console.log('Starting dispatchTrendsDataQuery, signal:', abortController.signal.aborted ? 'aborted' : 'active');
  const actionResult = dispatch(
    commonApi.endpoints.getTrendsData.initiate({
      ...params,
      signal: abortController.signal,
    })
  );
  const abort = () => {
    console.log('Aborting dispatchTrendsDataQuery, caller:');
    abortController.abort();
    actionResult.abort();
  };

  return {
    promise: actionResult
      .unwrap()
      .then((res) => {
        return res ? JSON.parse(JSON.stringify(res)) : undefined;
      })
      .catch((err) => {
        console.error('dispatchTrendsDataQuery error:', err);
        throw err;
      }),
    abort,
  };
}

export async function dispatchCRListDataQuery(dispatch, params, forceRefetch = false) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getCRList.initiate(params, { forceRefetch: forceRefetch }));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching trends-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchCustomerInfoQuery(dispatch) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getCustomerInfo.initiate(''));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching customer-info-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchGeneratePagePageContext(dispatch, params) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.generatePageContext.initiate(params));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching GeneratePagePageContext', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export async function dispatchGetSegmentDataWithType(dispatch, type) {
  try {
    const actionResult = await dispatch(commonApi.endpoints.getSegmentDataWithType.initiate(type));
    return actionResult.data ? JSON.parse(JSON.stringify(actionResult.data)) : undefined;
  } catch (error) {
    console.error('error fetching segment-data', error);
    throw error; // Rethrow the error if you want to handle it where the function is called
  }
}

export const {
  useGetMasterDataQuery,
  useGetCRListQuery,
  // useLazyGetMasterDataQuery,
  useGetSegmentDataWithTypeQuery,
  useGetCustomerInfoQuery,
  useGetTrendsDataQuery,
  // useLazyGetCustomerInfoQuery,
  useGeneratePageContextQuery,
  usePrefetch,
  middleware,
  reducer,
  endpoints,
  util,
  reducerPath,
} = commonApi;
export default commonApi;
